import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { LoadedContent } from "components/common/loading/loadedContent";
import { PopoutContainer } from "components/common/PopoutContainer";
import { DateStepper } from "components/DateStepper";
import { ScrapEyeLayoutMapMini } from "components/scrapEyeLayout/ScrapEyeLayoutMapMini";
import { AreaColorMap } from "components/scrapEyeLayout/scrapEyeLayoutCommon";
import { DayTimeline } from "components/manualReview/DayTimeline/DayTimeline";
import { eventToTimelineTick } from "components/manualReview/DayTimeline/timelineTick";
import { DEFAULT_WINDOW_MINUTES } from "components/manualReview/hooks/useManualReviewNavigation";
import { dayjsSerializer } from "components/manualReview/utils/timeUtils";
import { labellerDetailsSerialiser } from "components/manualReview/utils/labellerUtils";
import { HotkeyProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { TimelineZoomProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import {
  useVideoControl,
  VideoControlProvider,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useAreaLabelledEvents } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import { EventWarning } from "components/scrapEyeLabelling/types";
import { ModelPredictionsPanel } from "components/viewLabelsReview/ModelPredictionsPanel";
import { ViewLabelsVideoPlayer } from "components/viewLabelsReview/ViewLabelsVideoPlayer";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterialColor } from "hooks/useMaterials";
import { useTenant, useUserUid } from "hooks/settings";
import { useQueryParamState } from "hooks/useQueryParamState";
import { loadedEndpoint } from "models/loaded";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  HeatSummary,
  LabellerVerifiedEventsComparison,
  ReviewPeriodData,
  ReviewReason,
  VerifiedEventComparison,
  useGetLabellersQuery,
  useGetLabellerVerifiedEventsComparisonQuery,
  useListLocationExceptionalStatusesQuery,
  LabellerDetails,
} from "src/store/api/generatedApi";
import { REVIEW_REASON_VALUES } from "./reviewReasons";
import { VideoPlayerGroupProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoPlayerGroupContext";

dayjs.extend(utc);

const EMPTY_HEATS: HeatSummary[] = [];
const EMPTY_REVIEW_PERIODS: ReviewPeriodData[] = [];

export const ViewLabelsLocationPicker = () => {
  const { t } = useTenantTranslation();

  const { locationNames: locations, isLoading } = useLabellingLocations();

  const [selectedLocation, setSelectedLocation] = useState("");

  useEffect(() => {
    if (locations.length > 0 && !selectedLocation) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- bidirectional state with default selection
      setSelectedLocation(locations[0] ?? "");
    }
  }, [locations, selectedLocation]);

  return (
    <AccountPageLayout title={t("viewLabels")}>
      <Stack spacing={3} py={4}>
        <Typography variant="h5">{t("reviewYourLabels")}</Typography>
        <Typography variant="body1" color="text.secondary">
          {t("selectLocationToReviewYourLabels")}
        </Typography>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : locations.length === 0 ? (
          <Alert severity="info">{t("noLocationsConfigured")}</Alert>
        ) : (
          <FormControl fullWidth>
            <InputLabel id="location-select-label">{t("location")}</InputLabel>
            <Select
              labelId="location-select-label"
              value={selectedLocation}
              label={t("location")}
              onChange={(e) => {
                setSelectedLocation(e.target.value);
              }}
            >
              {locations.map((loc) => (
                <MenuItem key={loc} value={loc}>
                  {loc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {selectedLocation ? (
          <Button
            component={Link}
            to={selectedLocation}
            variant="contained"
            size="large"
          >
            {t("goToLocation", { location: selectedLocation })}
          </Button>
        ) : null}
      </Stack>
    </AccountPageLayout>
  );
};

export const ViewLabelsPage = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { location } = useParams<{ location: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const timeParam = searchParams.get("time");
  const modelParam = searchParams.get("model");
  const initialTime = useMemo(() => {
    if (!timeParam) return null;
    const parsed = dayjs.utc(timeParam);
    return parsed.isValid() ? parsed : null;
  }, [timeParam]);

  const [selectedDate, setSelectedDate] = useQueryParamState<Dayjs, never>(
    "date",
    dayjsSerializer,
    dayjs.utc().subtract(1, "day")
  );

  const [selectedLabeller, setSelectedLabeller] = useQueryParamState<
    LabellerDetails | null,
    unknown
  >("labeller", labellerDetailsSerialiser, null);

  const { data: labellers } = useGetLabellersQuery({
    tenantName,
  });

  const userUid = useUserUid();

  useEffect(() => {
    if (!labellers || labellers.length === 0 || selectedLabeller) return;

    if (modelParam) {
      const modelLabeller = labellers.find(
        (l) => l.labeller_type === "model" && l.name === modelParam
      );
      if (modelLabeller) {
        setSelectedLabeller(modelLabeller);
        return;
      }
    }

    if (userUid) {
      const userLabeller = labellers.find(
        (l) => l.id === userUid && l.labeller_type === "human"
      );
      if (userLabeller) {
        setSelectedLabeller(userLabeller);
      }
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- selectedLabeller and setSelectedLabeller omitted; adding them would cause infinite loops on mount
  }, [labellers, userUid, modelParam]);

  const dayStart = useMemo(
    () => dayjs.utc(selectedDate.format("YYYY-MM-DD")).startOf("day"),
    [selectedDate]
  );
  const dayEnd = useMemo(
    () => dayjs.utc(selectedDate.format("YYYY-MM-DD")).endOf("day"),
    [selectedDate]
  );

  const comparisonQuery = useGetLabellerVerifiedEventsComparisonQuery(
    {
      tenantName,
      location: location ?? "",
      windowStart: dayStart.toISOString(),
      windowEnd: dayEnd.toISOString(),
      labellerId: selectedLabeller?.id ?? "",
      labellerType: selectedLabeller?.labeller_type ?? "human",
      includeUnusedModelEvents: selectedLabeller?.labeller_type === "model",
    },
    {
      skip: !location || !selectedLabeller,
    }
  );

  const loadedComparison = loadedEndpoint(comparisonQuery);

  return (
    <VideoPlayerGroupProvider location={location ?? ""}>
      <HotkeyProvider>
        <VideoControlProvider>
          <AccountPageLayout sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  onClick={() =>
                    void navigate(`/${tenantName}/tool/view-labels`)
                  }
                  size="small"
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h1">{t("viewLabels")}</Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                {labellers && labellers.length > 0 ? (
                  <LabellerSelector
                    labellers={labellers}
                    selectedLabeller={selectedLabeller}
                    onLabellerChange={setSelectedLabeller}
                  />
                ) : null}
                <DateStepper
                  date={selectedDate}
                  setDate={setSelectedDate}
                  timezone="UTC"
                />
              </Stack>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {selectedLabeller ? (
                <LoadedContent
                  data={loadedComparison}
                  enableFullHeight
                  error={
                    <Alert severity="error">
                      {t("failedToLoadViewLabels")}
                    </Alert>
                  }
                >
                  {(comparison) => (
                    <ViewLabelsPageContent
                      comparison={comparison}
                      location={location ?? ""}
                      dayStart={dayStart}
                      dayEnd={dayEnd}
                      selectedLabeller={selectedLabeller}
                      initialTime={initialTime}
                    />
                  )}
                </LoadedContent>
              ) : null}
            </Box>
          </AccountPageLayout>
        </VideoControlProvider>
      </HotkeyProvider>
    </VideoPlayerGroupProvider>
  );
};

const LabellerSelector = ({
  labellers,
  selectedLabeller,
  onLabellerChange,
}: {
  labellers: LabellerDetails[];
  selectedLabeller: LabellerDetails | null;
  onLabellerChange: (labeller: LabellerDetails | null) => void;
}) => {
  const { t } = useTenantTranslation();

  return (
    <Autocomplete
      size="small"
      sx={{ minWidth: 200 }}
      options={labellers}
      getOptionLabel={(option) =>
        option.name ?? option.id + " (" + option.labeller_type + ")"
      }
      value={selectedLabeller}
      onChange={(_, newValue) => {
        onLabellerChange(newValue);
      }}
      renderInput={(params) => (
        <TextField {...params} label={t("viewAsLabeller")} />
      )}
      isOptionEqualToValue={(option, value) =>
        option.id === value.id && option.labeller_type === value.labeller_type
      }
    />
  );
};

const ViewLabelsPageContent = ({
  comparison,
  location,
  dayStart,
  dayEnd,
  selectedLabeller,
  initialTime,
}: {
  comparison: LabellerVerifiedEventsComparison;
  location: string;
  dayStart: Dayjs;
  dayEnd: Dayjs;
  selectedLabeller: LabellerDetails;
  initialTime: Dayjs | null;
}) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const withAreaLabels = useAreaLabelledEvents(location);

  const {
    state: { currentTime },
  } = useVideoControl();

  const { data: exceptionalStatusData } =
    useListLocationExceptionalStatusesQuery({
      tenantName,
      locationName: location,
    });

  const [viewingWindowStart, setViewingWindowStart] = useState<Dayjs>(() => {
    if (
      initialTime &&
      !initialTime.isBefore(dayStart) &&
      !initialTime.isAfter(dayEnd)
    ) {
      return initialTime;
    }
    const firstComparisonWithEvent = comparison.comparisons.find(
      (c) => c.comparison_event !== null
    );
    if (firstComparisonWithEvent?.comparison_event) {
      return dayjs(firstComparisonWithEvent.comparison_event.data.start);
    }
    return dayStart;
  });

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedReasons, setSelectedReasons] = useState<ReviewReason[]>([]);

  const viewingWindowEnd = useMemo(
    () => viewingWindowStart.add(DEFAULT_WINDOW_MINUTES, "minutes"),
    [viewingWindowStart]
  );

  const comparisonsByEventId = useMemo(() => {
    const map = new Map<number, VerifiedEventComparison>();
    for (const comp of comparison.comparisons) {
      if (comp.comparison_event) {
        map.set(comp.comparison_event.id, comp);
      }
    }
    return map;
  }, [comparison.comparisons]);

  const comparisonsByVerifiedEventId = useMemo(() => {
    const map = new Map<number, VerifiedEventComparison>();
    for (const comp of comparison.comparisons) {
      if (comp.verified_event) {
        map.set(comp.verified_event.id, comp);
      }
    }
    return map;
  }, [comparison.comparisons]);

  const allEvents = useMemo(() => {
    return [
      ...comparison.all_labeller_events,
      ...comparison.all_verified_events,
    ];
  }, [comparison.all_labeller_events, comparison.all_verified_events]);

  const comparisonEventsInWindow = useMemo(() => {
    return comparison.all_labeller_events.filter((event) => {
      const eventStart = dayjs(event.data.start);
      const eventEnd = dayjs(event.data.end);
      return (
        eventEnd.isAfter(viewingWindowStart) &&
        eventStart.isBefore(viewingWindowEnd)
      );
    });
  }, [comparison.all_labeller_events, viewingWindowStart, viewingWindowEnd]);

  const formattedEvents = useMemo(() => {
    const events = comparisonEventsInWindow
      .map((e) => {
        const material =
          e.labels.find((l) => l.data.label_type === "material")?.data.label ??
          null;
        const comp = comparisonsByEventId.get(e.id);
        const hasMatchingConflict =
          comp &&
          comp.conflict_reasons.length > 0 &&
          (selectedReasons.length === 0 ||
            selectedReasons.some((r) => comp.conflict_reasons.includes(r)));
        const warnings: EventWarning[] = hasMatchingConflict
          ? ["materialMismatchWithActual"]
          : [];
        return {
          id: String(e.id),
          start: dayjs(e.data.start),
          end: dayjs(e.data.end),
          eventType: e.data.event_type,
          material,
          area:
            e.labels.find((l) => l.data.label_type === "area")?.data.label ??
            null,
          pairId: null as string | null,
          warnings,
        };
      })
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());

    const pairedEvents = events.map((event, index) => {
      if (event.pairId !== null || event.eventType !== "lift") return event;
      const nextEvent = events[index + 1];
      if (
        nextEvent?.eventType === "drop" &&
        nextEvent.material === event.material &&
        !nextEvent.pairId
      ) {
        return { ...event, pairId: uuidv4() };
      }
      return event;
    });

    return withAreaLabels(
      pairedEvents.map((event, index) => {
        if (event.eventType === "drop" && !event.pairId) {
          const prevEvent = pairedEvents[index - 1];
          if (
            prevEvent?.eventType === "lift" &&
            prevEvent.pairId &&
            prevEvent.material === event.material
          ) {
            return { ...event, pairId: prevEvent.pairId };
          }
        }
        return event;
      })
    );
  }, [
    comparisonEventsInWindow,
    comparisonsByEventId,
    selectedReasons,
    withAreaLabels,
  ]);

  const windowDuration = useMemo(
    () => viewingWindowEnd.diff(viewingWindowStart, "second"),
    [viewingWindowStart, viewingWindowEnd]
  );

  const verifiedEventsInWindow = useMemo(() => {
    return comparison.all_verified_events.filter((event) => {
      const eventStart = dayjs(event.data.start);
      const eventEnd = dayjs(event.data.end);
      return (
        eventEnd.isAfter(viewingWindowStart) &&
        eventStart.isBefore(viewingWindowEnd)
      );
    });
  }, [comparison.all_verified_events, viewingWindowStart, viewingWindowEnd]);

  const formattedVerifiedEvents = useMemo(() => {
    return withAreaLabels(
      verifiedEventsInWindow.map((e) => {
        const comp = comparisonsByVerifiedEventId.get(e.id);
        const hasMatchingConflict =
          comp &&
          comp.conflict_reasons.length > 0 &&
          (selectedReasons.length === 0 ||
            selectedReasons.some((r) => comp.conflict_reasons.includes(r)));
        const warnings: EventWarning[] = hasMatchingConflict
          ? ["materialMismatchWithActual"]
          : [];
        return {
          id: String(e.id),
          start: dayjs(e.data.start),
          end: dayjs(e.data.end),
          eventType: e.data.event_type,
          material:
            e.labels.find((l) => l.data.label_type === "material")?.data
              .label ?? null,
          area:
            e.labels.find((l) => l.data.label_type === "area")?.data.label ??
            null,
          warnings,
        };
      })
    );
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- body reads comparisonsByVerifiedEventId but depends on comparisonsByEventId because both are derived from the same comparison data
  }, [
    verifiedEventsInWindow,
    comparisonsByEventId,
    selectedReasons,
    withAreaLabels,
  ]);

  const filteredAllEvents = useMemo(() => {
    if (selectedReasons.length === 0) return allEvents;
    const filteredComparisonEvents = comparison.all_labeller_events.filter(
      (e) => {
        const comp = comparisonsByEventId.get(e.id);
        return (
          comp && selectedReasons.some((r) => comp.conflict_reasons.includes(r))
        );
      }
    );
    const filteredVerifiedEvents = comparison.all_verified_events.filter(
      (e) => {
        const comp = comparisonsByVerifiedEventId.get(e.id);
        return (
          comp && selectedReasons.some((r) => comp.conflict_reasons.includes(r))
        );
      }
    );
    return [...filteredComparisonEvents, ...filteredVerifiedEvents];
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- comparison.all_labeller_events and comparison.all_verified_events omitted; comparison is an object ref whose sub-properties would cause re-renders
  }, [
    allEvents,
    selectedReasons,
    comparisonsByEventId,
    comparisonsByVerifiedEventId,
  ]);

  const timelineTicks = useMemo(
    () => filteredAllEvents.map(eventToTimelineTick),
    [filteredAllEvents]
  );

  const [selectedComparison, setSelectedComparison] =
    useState<VerifiedEventComparison | null>(null);

  const handleEventClick = useCallback(
    (eventId: number) => {
      setSelectedEventId(String(eventId));
      const comp = comparisonsByEventId.get(eventId);
      if (comp && comp.conflict_reasons.length > 0) {
        setSelectedComparison(comp);
      }
      const event = allEvents.find((e) => e.id === eventId);
      if (event) {
        const eventStart = dayjs(event.data.start);
        if (
          eventStart.isBefore(viewingWindowStart) ||
          eventStart.isAfter(viewingWindowEnd)
        ) {
          const maxStart = dayEnd.subtract(DEFAULT_WINDOW_MINUTES, "minutes");
          const newStart = eventStart.isAfter(maxStart) ? maxStart : eventStart;
          setViewingWindowStart(newStart);
        }
      }
    },
    [
      allEvents,
      comparisonsByEventId,
      viewingWindowStart,
      viewingWindowEnd,
      dayEnd,
    ]
  );

  const handleWindowChange = useCallback(
    (newStart: Dayjs) => {
      const maxStart = dayEnd.subtract(DEFAULT_WINDOW_MINUTES, "minutes");
      const clampedStart = newStart.isBefore(dayStart)
        ? dayStart
        : newStart.isAfter(maxStart)
          ? maxStart
          : newStart;
      setViewingWindowStart(clampedStart);
    },
    [dayStart, dayEnd]
  );

  const { getColor } = useMaterialColor();

  const videoCurrentTime = useMemo(
    () => viewingWindowStart.add(currentTime, "second"),
    [viewingWindowStart, currentTime]
  );

  const nextEvent = useMemo(() => {
    return formattedEvents.find(
      (e) =>
        e.start.isAfter(videoCurrentTime) ||
        (e.start.isBefore(videoCurrentTime) && e.end.isAfter(videoCurrentTime))
    );
  }, [formattedEvents, videoCurrentTime]);

  const nextDestination = nextEvent?.area ?? null;

  const areaColors = useMemo((): AreaColorMap => {
    const colors: Record<string, string> = {};
    for (const event of allEvents) {
      const area = event.labels.find((l) => l.data.label_type === "area")?.data
        .label;
      const material = event.labels.find(
        (l) => l.data.label_type === "material"
      )?.data.label;
      if (area && material) {
        const color = getColor(material);
        if (color) {
          colors[area] = color;
        }
      }
    }
    return colors;
  }, [allEvents, getColor]);

  if (comparison.comparisons.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <Alert severity="info">{t("noLabelsFoundForDate")}</Alert>
      </Box>
    );
  }

  return (
    <TimelineZoomProvider
      fullDuration={windowDuration}
      videoStart={viewingWindowStart}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          height: "100%",
          gap: 2,
        }}
      >
        <Stack
          direction="column"
          flex={1}
          minWidth={0}
          position="relative"
          spacing={0.5}
        >
          <DayTimeline
            viewingWindowStart={viewingWindowStart}
            viewingWindowEnd={viewingWindowEnd}
            heats={EMPTY_HEATS}
            ticks={timelineTicks}
            reviewPeriods={EMPTY_REVIEW_PERIODS}
            exceptionalStatuses={exceptionalStatusData?.items ?? []}
            onWindowChange={handleWindowChange}
            dayStart={dayStart}
            dayEnd={dayEnd}
          />

          <Stack
            direction="row"
            spacing={2}
            mb={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {t("labellerName")}:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {comparison.labeller.name ?? comparison.labeller.id}
              </Typography>
            </Stack>
            <Autocomplete
              multiple
              size="small"
              sx={{ minWidth: 300 }}
              options={REVIEW_REASON_VALUES.filter(
                (reason) => reason !== "likely_error"
              )}
              value={selectedReasons}
              onChange={(_, newValue) => {
                setSelectedReasons(newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} label={t("filterByReason")} />
              )}
            />
          </Stack>
          <ViewLabelsVideoPlayer
            location={location}
            startIso={viewingWindowStart.toISOString()}
            endIso={viewingWindowEnd.toISOString()}
            events={formattedEvents}
            verifiedEvents={formattedVerifiedEvents}
            selectedEventId={selectedEventId}
            onEventClick={(id) => {
              handleEventClick(Number(id));
            }}
            videoStart={viewingWindowStart}
            labellingName={
              selectedLabeller.labeller_type === "model"
                ? selectedLabeller.id
                : undefined
            }
          />
          {selectedLabeller.labeller_type === "model" ? (
            <ModelPredictionsPanel
              labellingName={selectedLabeller.id}
              startIso={viewingWindowStart.toISOString()}
              endIso={viewingWindowEnd.toISOString()}
              videoStart={viewingWindowStart}
            />
          ) : null}
        </Stack>
        <Box
          sx={{
            width: 280,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
            borderRadius: 1,
            p: 1,
          }}
        >
          <PopoutContainer
            title={t("scrapLayout")}
            poppedOutMessage={t("layoutPoppedOut")}
          >
            <ScrapEyeLayoutMapMini
              location={location}
              highlightedArea={nextDestination}
              areaColors={areaColors}
            />
          </PopoutContainer>
        </Box>
      </Box>
      <ComparisonDialog
        comparison={selectedComparison}
        onClose={() => {
          setSelectedComparison(null);
        }}
      />
    </TimelineZoomProvider>
  );
};

const ComparisonDialog = ({
  comparison,
  onClose,
}: {
  comparison: VerifiedEventComparison | null;
  onClose: () => void;
}) => {
  const { t } = useTenantTranslation();

  if (!comparison) return null;

  const labellerEvent = comparison.comparison_event;
  const verifiedEvent = comparison.verified_event;

  const labelledMaterial = labellerEvent
    ? (labellerEvent.labels.find((l) => l.data.label_type === "material")?.data
        .label ?? t("noMaterial"))
    : t("missing");

  const verifiedMaterial = verifiedEvent
    ? (verifiedEvent.labels.find((l) => l.data.label_type === "material")?.data
        .label ?? t("noMaterial"))
    : t("unknown");

  const labelledArea = labellerEvent
    ? (labellerEvent.labels.find((l) => l.data.label_type === "area")?.data
        .label ?? t("noArea"))
    : t("missing");

  const verifiedArea = verifiedEvent
    ? (verifiedEvent.labels.find((l) => l.data.label_type === "area")?.data
        .label ?? t("noArea"))
    : t("unknown");

  const sourceEvent = labellerEvent ?? verifiedEvent;
  const eventType = sourceEvent?.data.event_type ?? "event";
  const eventTime = sourceEvent
    ? dayjs(sourceEvent.data.start).format("HH:mm:ss")
    : t("unknown");

  return (
    <Dialog open={!!comparison} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("materialMismatch")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t("eventType")}:
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {eventType.toUpperCase()}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t("time")}:
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {eventTime}
            </Typography>
          </Stack>
          <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("labelledMaterial")}:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {labelledMaterial}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("verifiedMaterial")}:
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="error.main">
                {verifiedMaterial}
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("labelledArea")}:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {labelledArea}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("verifiedArea")}:
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="error.main">
                {verifiedArea}
              </Typography>
            </Stack>
          </Box>
          {comparison.conflict_reasons.length > 0 ? (
            <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("conflictReasons")}:
              </Typography>
              {comparison.conflict_reasons.map((reason) => (
                <Typography key={reason} variant="body2">
                  {reason}
                </Typography>
              ))}
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("close")}</Button>
      </DialogActions>
    </Dialog>
  );
};
